import React, { useState } from "react";
import { ChevronDown, Info, AlertTriangle, Fingerprint, Pause, History, XCircle, CheckCircle2, Play } from "lucide-react";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";

const STORAGE_KEY = "advoset.admin.identities.guideOpen";

const TABS = [
  { icon: AlertTriangle, name: "Conflicts", text: "Same email or phone claimed by more than one account (e.g. a lawyer applying with a phone already on a user account), plus identities an admin has quarantined. Should normally be empty." },
  { icon: Fingerprint, name: "All identities", text: "Every claim in the registry — one row per email/phone per account, with its status (Active, Pending, Rejected, Suspended, Quarantined)." },
  { icon: Pause, name: "Suspended", text: "Identities an admin has blocked. The owner cannot sign in with that email/phone until it is reactivated." },
];

const ACTIONS = [
  { icon: History, name: "History", text: "Read-only audit trail of the identity: every claim, release, suspension, quarantine and resolution, with who did it and why." },
  { icon: Pause, name: "Suspend", text: "Blocks sign-in with this email/phone immediately (reason required). Reversible. Use for abusive or compromised accounts." },
  { icon: Play, name: "Reactivate", text: "Lifts a suspension or quarantine and restores sign-in." },
  { icon: AlertTriangle, name: "Quarantine", text: "Freezes authentication on a disputed identity while you investigate. It then appears under Conflicts until resolved." },
  { icon: CheckCircle2, name: "Approve claim", text: "Resolves a conflict in favour of this account: its claim becomes Active and every competing claim on the same email/phone is rejected." },
  { icon: XCircle, name: "Reject claim", text: "Resolves a conflict against this account: its claim is marked Rejected and it loses the email/phone; competing claims are left untouched." },
];

function Row({ icon: Icon, name, text, testid }) {
  return (
    <li className="flex gap-3" data-testid={testid}>
      <span className="mt-0.5 h-6 w-6 shrink-0 grid place-items-center rounded-md bg-[#0A2540]/5 text-[#0A2540]"><Icon size={13} /></span>
      <p className="text-xs text-slate-600 leading-relaxed"><span className="font-bold text-[#0A2540]">{name}</span> — {text}</p>
    </li>
  );
}

export function IdentitiesGuide() {
  const [open, setOpen] = useState(() => window.localStorage.getItem(STORAGE_KEY) !== "0");
  const onChange = (v) => { setOpen(v); window.localStorage.setItem(STORAGE_KEY, v ? "1" : "0"); };
  return (
    <Collapsible open={open} onOpenChange={onChange}>
      <section className="bg-white border border-slate-200 rounded-2xl mb-5" data-testid="identities-guide" data-state={open ? "open" : "closed"}>
        <CollapsibleTrigger asChild>
          <button type="button" data-testid="identities-guide-toggle" aria-expanded={open}
            className="w-full flex items-center gap-3 px-5 py-3.5 text-left rounded-2xl hover:bg-slate-50 transition-colors">
            <Info size={16} className="text-[#D4AF37] shrink-0" />
            <span className="flex-1">
              <span className="block text-sm font-bold text-[#0A2540]">How this page works</span>
              <span className="block text-xs text-slate-500">What the tabs mean and what each action does. Nothing here changes data.</span>
            </span>
            <ChevronDown size={16} className={`text-slate-400 transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="px-5 pb-5 grid gap-6 lg:grid-cols-2" data-testid="identities-guide-body">
            <div>
              <p className="text-[11px] uppercase tracking-widest font-bold text-slate-400 mb-2">Purpose</p>
              <p className="text-xs text-slate-600 leading-relaxed mb-4">
                The registry records which account owns each email and phone across users, lawyers and admins. It is what prevents
                two accounts from sharing an identifier. Approved Change Requests re-point these claims; suspending or deleting a
                lawyer suspends or releases theirs automatically.
              </p>
              <p className="text-[11px] uppercase tracking-widest font-bold text-slate-400 mb-2">Tabs</p>
              <ul className="space-y-2.5">{TABS.map((t) => <Row key={t.name} {...t} testid={`identities-guide-tab-${t.name.toLowerCase().replace(/\s+/g, "-")}`} />)}</ul>
            </div>
            <div>
              <p className="text-[11px] uppercase tracking-widest font-bold text-slate-400 mb-2">Actions (every write asks for a reason and is audited)</p>
              <ul className="space-y-2.5">{ACTIONS.map((a) => <Row key={a.name} {...a} testid={`identities-guide-action-${a.name.toLowerCase().replace(/\s+/g, "-")}`} />)}</ul>
            </div>
          </div>
        </CollapsibleContent>
      </section>
    </Collapsible>
  );
}
